import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Divider,
  Grid,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import ContactAvatarWithDetails from "./ContactDetails/ContactAvatarWithDetails";
import FamilyHeaderRow from "./ContactDetails/FamilyHeaderRow";
import ContactTabCard from "./ContactDetails/ContactTabCard";
import ContactEmploymentInfo from "./ContactDetails/ContactEmploymentInfo";
import ContactAdditionalInfo from "./ContactDetails/ContactAdditionalInfo";
import { MimshakData } from "../../interfaces/DTO/mimshakData";
import { Contact } from "../../interfaces/DTO/crm/contact";
import { Family } from "../../interfaces/DTO/crm/family";
import { usePartialUpdateContact, useContacts } from "../../hooks/crm/useContacts";
import { usePartialUpdateFamily, useFamilyById } from "../../hooks/crm/useFamilies";
import { useIdStore } from "../../stores/selectedToViewStore";
import { MimshakContactUtil } from "../../services/mimshakContactUtil";
import WorkIcon from "@mui/icons-material/Work";
import InfoIcon from "@mui/icons-material/Info";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import InsuranceClientQuestionnaire from "../PersonFinancialData/Insurance/ClientQuestionnaire";
import ContactPortalAccessCard from "../CRM/contacts/ContactPortalAccessCard";
import ContactBeneficiariesTab from "../CRM/contacts/beneficiaries/ContactBeneficiariesTab";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ContactBankAccountsTab from "../CRM/contacts/bankAccounts/ContactBankAccountsTab";

type Section = "contact" | "beneficiaries" | "bank" | "employment" | "additional" | "insurance" | "portal";

const NAV_ITEMS: { key: Section; label: string; icon: React.ReactNode }[] = [
  { key: "contact",    label: "לקוח",       icon: <PersonIcon /> },
  { key: "employment",label: "תעסוקה",     icon: <WorkIcon /> },
  { key: "additional",label: "פרטים נוספים", icon: <InfoIcon /> },
  { key: "beneficiaries", label: "מוטבים", icon: <VolunteerActivismIcon /> },
  { key: "bank",      label: "חשבונות בנק", icon: <AccountBalanceIcon /> },
  { key: "insurance", label: "מידע לביטוח",  icon: <HealthAndSafetyIcon /> },
  { key: "portal",    label: "אפליקציית REV", icon: <PhoneIphoneIcon /> },
];

interface ConsultingContactInfoProps {
  open: boolean;
  onClose: () => void;
  allMimshakData?: MimshakData[];
  contacts: Contact[];
  initialSection?: Section;
  family?: Family;
  viewedType?: "USER" | "FAMILY";
}

const ConsultingContactInfo: React.FC<ConsultingContactInfoProps> = ({
  open,
  onClose,
  allMimshakData,
  contacts,
  initialSection = "contact",
  family,
  viewedType,
}) => {
  const [activeSection, setActiveSection] = useState<Section>(initialSection);
  // a phone - the sections in a menu next to the title (no room for the side nav)
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [navAnchor, setNavAnchor] = useState<HTMLElement | null>(null);
  // The family_portal_consent row is shared between both contacts of a pair — bump this to force
  // both ContactPortalAccessCard instances in the "portal" section to remount and reload after
  // either one's enable/clear action, since they're both looking at the same underlying row.
  const [portalRefreshKey, setPortalRefreshKey] = useState(0);

  const updateContact = usePartialUpdateContact();
  const updateFamily = usePartialUpdateFamily();
  const { data: allContacts } = useContacts();

  const storeId = useIdStore((state) => state.id);
  const { data: familyFromStore } = useFamilyById(viewedType === "FAMILY" ? storeId : 0);
  const resolvedFamily = family ?? familyFromStore;

  // sync initialSection when drawer re-opens
  useEffect(() => {
    if (open) setActiveSection(initialSection);
  }, [open, initialSection]);

  // Use live data from cache, falling back to prop
  const liveContacts = useMemo(() => {
    if (!allContacts) return contacts;
    const contactIds = new Set(contacts.map((c) => c.id));
    return allContacts.filter((c) => contactIds.has(c.id));
  }, [allContacts, contacts]);

  const sortedContacts = useMemo(() => {
    return [...liveContacts].sort((a, b) => (a.id || 0) - (b.id || 0));
  }, [liveContacts]);

  const contact1 = sortedContacts[0];
  const contact2 = sortedContacts[1];

  const contact1MimshakData = useMemo(() => {
    if (!contact1 || !allMimshakData) return [];
    return allMimshakData.filter((md) => md.contact_id === contact1.id);
  }, [allMimshakData, contact1]);

  const contact2MimshakData = useMemo(() => {
    if (!contact2 || !allMimshakData) return [];
    return allMimshakData.filter((md) => md.contact_id === contact2.id);
  }, [allMimshakData, contact2]);

  const contact1DifferentInfo = useMemo(() => {
    if (!contact1 || !allMimshakData) return { client_names: [], last_names: [], phones: [], emails: [] };
    const mimshakInfo = MimshakContactUtil.getDistinctContactInfoByContactId(allMimshakData, contact1.id!);
    const differentNames = MimshakContactUtil.getDifferentNames(
      contact1.first_name || "", contact1.last_name || "",
      mimshakInfo.client_names, mimshakInfo.last_names
    );
    return {
      client_names: differentNames.client_names,
      last_names: differentNames.last_names,
      phones: MimshakContactUtil.getDifferentPhones(contact1.phone || "", mimshakInfo.phones),
      emails: MimshakContactUtil.getDifferentEmails(contact1.email || "", mimshakInfo.emails),
    };
  }, [allMimshakData, contact1]);

  const contact2DifferentInfo = useMemo(() => {
    if (!contact2 || !allMimshakData) return { client_names: [], last_names: [], phones: [], emails: [] };
    const mimshakInfo = MimshakContactUtil.getDistinctContactInfoByContactId(allMimshakData, contact2.id!);
    const differentNames = MimshakContactUtil.getDifferentNames(
      contact2.first_name || "", contact2.last_name || "",
      mimshakInfo.client_names, mimshakInfo.last_names
    );
    return {
      client_names: differentNames.client_names,
      last_names: differentNames.last_names,
      phones: MimshakContactUtil.getDifferentPhones(contact2.phone || "", mimshakInfo.phones),
      emails: MimshakContactUtil.getDifferentEmails(contact2.email || "", mimshakInfo.emails),
    };
  }, [allMimshakData, contact2]);

  const handleConfirmIdentification = async (contactId: number, contact: Contact) => {
    await updateContact.mutateAsync({ id: contactId, fields: { data: { ...contact.data, identification_time_stamp: new Date().toISOString() } } });
  };

  const handleClearIdentification = async (contactId: number, contact: Contact) => {
    await updateContact.mutateAsync({ id: contactId, fields: { data: { ...contact.data, identification_time_stamp: undefined } } });
  };

  const handleSaveContactFields = async (contactId: number, fields: Partial<Contact>) => {
    await updateContact.mutateAsync({ id: contactId, fields });
  };

  const handleSaveHeader = async ({ familyName, maritalStatus }: { familyName?: string; maritalStatus?: number }) => {
    const updates: Promise<unknown>[] = [];
    if (familyName !== undefined && resolvedFamily)
      updates.push(updateFamily.mutateAsync({ id: resolvedFamily.id, fields: { name: familyName } }));
    if (maritalStatus !== undefined)
      sortedContacts
        .filter((c) => c.marital_status !== maritalStatus)
        .forEach((c) => updates.push(updateContact.mutateAsync({ id: c.id!, fields: { marital_status: maritalStatus } })));
    await Promise.all(updates);
  };

  /** the clients side by side (a couple), each in its own card - like the client tab */
  const renderPerContact = (
    render: (contact: Contact, other?: Contact) => React.ReactNode,
    maxWidth = 1040,
    /** both cards as tall as the taller one */
    equalHeight = false
  ) => {
    const all = [contact1, contact2].filter((c): c is Contact => !!c);
    return (
      <Box sx={{ maxWidth: all.length > 1 ? maxWidth : maxWidth / 2, mx: "auto" }}>
        <Grid container spacing={2} alignItems={equalHeight ? "stretch" : "flex-start"}>
          {all.map((c) => (
            <Grid item xs={12} md={all.length > 1 ? 6 : 12} key={c.id} sx={equalHeight ? { display: "flex" } : undefined}>
              {render(c, all.find((o) => o.id !== c.id))}
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  const renderContent = () => {
    switch (activeSection) {
      case "contact":
        return (
          <>
          <Box sx={{ maxWidth: contact2 ? 1040 : 520, mx: "auto" }}>
            <Box sx={{ mb: 2.5 }}>
              <FamilyHeaderRow
                familyName={viewedType === "FAMILY" && resolvedFamily ? resolvedFamily.name ?? "" : undefined}
                contacts={sortedContacts}
                onSave={handleSaveHeader}
              />
            </Box>
            <Grid container spacing={2} alignItems="flex-start">
              {[contact1, contact2].filter((c): c is Contact => !!c).map((c, _i, all) => (
                <Grid item xs={12} md={all.length > 1 ? 6 : 12} key={c.id}>
                  <ContactAvatarWithDetails
                    contact={c}
                    spouse={all.find((other) => other.id !== c.id)}
                    mimshakData={allMimshakData ?? []}
                    onSave={handleSaveContactFields}
                    onConfirmIdentification={() => handleConfirmIdentification(c.id!, c)}
                    onClearIdentification={() => handleClearIdentification(c.id!, c)}
                    differentInfo={c.id === contact1?.id ? contact1DifferentInfo : contact2DifferentInfo}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
          </>
        );

      case "employment":
        return (
          <Box sx={{ maxWidth: contact2 ? 1040 : 520, mx: "auto" }}>
            <Grid container spacing={2} alignItems="flex-start">
              {[contact1, contact2].filter((c): c is Contact => !!c).map((c, _i, all) => (
                <Grid item xs={12} md={all.length > 1 ? 6 : 12} key={c.id}>
                  <ContactEmploymentInfo
                    contact={c}
                    contactMimshakData={c.id === contact1?.id ? contact1MimshakData : contact2MimshakData}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        );

      case "additional":
        return renderPerContact((c) => <ContactAdditionalInfo contact={c} />);

      case "beneficiaries":
        // a couple - each one's spouse is the other
        return renderPerContact(
          (c, other) => (
            <ContactTabCard contact={c}>
              <ContactBeneficiariesTab contact={c} spouse={other} />
            </ContactTabCard>
          ),
          1400,
          true
        );

      case "bank":
        // a couple - a joint account is entered once (on either one) and kept on both
        return renderPerContact(
          (c, other) => (
            <ContactTabCard contact={c}>
              <ContactBankAccountsTab contact={c} spouse={other} />
            </ContactTabCard>
          ),
          1400,
          true
        );

      case "insurance":
        return (
          <Box sx={{ display: contact2 ? "flex" : "block", gap: 2 }}>
            {contact1 && (
              <Box sx={{ flex: 1 }}>
                {contact2 && (
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                    {[contact1.first_name, contact1.last_name].filter(Boolean).join(" ")}
                  </Typography>
                )}
                <InsuranceClientQuestionnaire contactId={contact1.id} defaultExpanded />
              </Box>
            )}
            {contact2 && (
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                  {[contact2.first_name, contact2.last_name].filter(Boolean).join(" ")}
                </Typography>
                <InsuranceClientQuestionnaire contactId={contact2.id} defaultExpanded />
              </Box>
            )}
          </Box>
        );

      case "portal": {
        // Per-contact — each card's siblingContacts must exclude that contact itself, otherwise
        // the "exactly one eligible member" path in ContactPortalAccessCard never triggers (it
        // sees itself + the real sibling = 2 "eligible" members) and falls back to the dropdown.
        const buildFamilyInfo = (excludeContactId: number | undefined) =>
          resolvedFamily
            ? {
                familyId: resolvedFamily.id,
                familyName: resolvedFamily.name,
                siblingContacts: sortedContacts
                  .filter((c): c is Contact & { id: number } => c.id !== undefined && c.id !== excludeContactId)
                  .map((c) => ({
                    id: c.id,
                    first_name: c.first_name || "",
                    last_name: c.last_name || "",
                    family_id: resolvedFamily.id,
                  })),
              }
            : undefined;

        return (
          <Box sx={{ display: contact2 ? "flex" : "block", gap: 2 }}>
            {contact1 && contact1.id && (
              <Box sx={{ flex: 1 }}>
                {contact2 && (
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                    {[contact1.first_name, contact1.last_name].filter(Boolean).join(" ")}
                  </Typography>
                )}
                <ContactPortalAccessCard
                  key={`${contact1.id}-${portalRefreshKey}`}
                  contactId={contact1.id}
                  familyInfo={buildFamilyInfo(contact1.id)}
                  onFamilySharingChanged={() => setPortalRefreshKey((k) => k + 1)}
                />
              </Box>
            )}
            {contact2 && contact2.id && (
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                  {[contact2.first_name, contact2.last_name].filter(Boolean).join(" ")}
                </Typography>
                <ContactPortalAccessCard
                  key={`${contact2.id}-${portalRefreshKey}`}
                  contactId={contact2.id}
                  familyInfo={buildFamilyInfo(contact2.id)}
                  onFamilySharingChanged={() => setPortalRefreshKey((k) => k + 1)}
                />
              </Box>
            )}
          </Box>
        );
      }
    }
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { height: "85vh", borderTopLeftRadius: 16, borderTopRightRadius: 16 } }}
    >
      <Box sx={{ display: "flex", height: "100%", direction: "rtl" }}>

        {/* Right sidebar nav - on a phone there is no room for it: a menu next to the title instead */}
        {!isMobile && (
        <Box sx={{ width: 130, borderLeft: "1px solid", borderColor: "divider", flexShrink: 0, pt: 2 }}>
          <List dense disablePadding>
            {NAV_ITEMS.map(({ key, label, icon }) => (
              <ListItemButton
                key={key}
                selected={activeSection === key}
                onClick={() => setActiveSection(key)}
                sx={{
                  flexDirection: "column",
                  alignItems: "center",
                  py: 2,
                  gap: 0.5,
                  "&.Mui-selected": { backgroundColor: "primary.light", color: "primary.contrastText" },
                  "&.Mui-selected .MuiListItemIcon-root": { color: "primary.contrastText" },
                }}
              >
                <ListItemIcon sx={{ minWidth: 0, justifyContent: "center" }}>{icon}</ListItemIcon>
                <ListItemText primary={label} primaryTypographyProps={{ variant: "caption", textAlign: "center" }} />
              </ListItemButton>
            ))}
          </List>
        </Box>
        )}

        {/* Main content */}
        <Box sx={{ flex: 1, minWidth: 0, overflow: "auto", p: { xs: 1.5, sm: 3 } }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {isMobile && (
                <IconButton onClick={(e) => setNavAnchor(e.currentTarget)} aria-label="תפריט">
                  <MenuIcon />
                </IconButton>
              )}
              <Typography variant="h6" fontWeight={600}>
                {NAV_ITEMS.find((n) => n.key === activeSection)?.label}
              </Typography>
            </Box>
            <IconButton onClick={onClose}><CloseIcon /></IconButton>
          </Box>
          <Menu anchorEl={navAnchor} open={!!navAnchor} onClose={() => setNavAnchor(null)} dir="rtl">
            {NAV_ITEMS.map(({ key, label, icon }) => (
              <MenuItem
                key={key}
                selected={activeSection === key}
                onClick={() => { setActiveSection(key); setNavAnchor(null); }}
              >
                <ListItemIcon>{icon}</ListItemIcon>
                <ListItemText primary={label} />
              </MenuItem>
            ))}
          </Menu>
          <Divider sx={{ mb: 3 }} />
          {renderContent()}
        </Box>

      </Box>
    </Drawer>
  );
};

export default ConsultingContactInfo;
